// Browser tab: tree-style view of the garage bucket using the
// delimiter-/ listing. Click a directory to descend, click an object to
// open it in a new tab through /api/objects/{key}, which streams it from
// garage under this session. (A presigned URL would name the in-cluster
// garage endpoint, which a browser outside the cluster cannot reach.)

import { useEffect, useState } from 'react';
import { api, ListPage, ObjectInfo, humanBytes } from './api';

export default function Browser() {
  const [prefix, setPrefix] = useState<string>('');
  const [page, setPage] = useState<ListPage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    api.listGarage(prefix)
      .then(setPage)
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, [prefix]);

  const crumbs = breadcrumbs(prefix);
  const dirs = page?.prefixes ?? [];
  const objs = page?.objects ?? [];

  return (
    <div className="browser">
      <div style={{ marginBottom: '0.5rem' }}>
        {crumbs.map((c, i) => (
          <span key={c.value}>
            {i > 0 && <span style={{ opacity: 0.5 }}> / </span>}
            <span className="crumb" onClick={() => setPrefix(c.value)}>{c.label}</span>
          </span>
        ))}
      </div>

      {err && <div className="error-banner">{err}</div>}
      {loading && <div>loading…</div>}

      {!loading && !err && (
        <table>
          <thead>
            <tr>
              <th>name</th>
              <th style={{ width: '8rem', textAlign: 'right' }}>size</th>
              <th style={{ width: '11rem' }}>modified</th>
              <th style={{ width: '6rem' }}></th>
            </tr>
          </thead>
          <tbody>
            {dirs.length === 0 && objs.length === 0 && (
              <tr><td colSpan={4} style={{ opacity: 0.6 }}>empty</td></tr>
            )}
            {dirs.map((d) => (
              <tr key={d} className="dir">
                <td>
                  <span className="crumb" onClick={() => setPrefix(d)}>
                    {trimPrefix(d, prefix) || d}
                  </span>
                </td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            ))}
            {objs.map((o) => (
              <ObjectRow key={o.key} obj={o} prefix={prefix} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ObjectRow({
  obj,
  prefix,
}: {
  obj: ObjectInfo;
  prefix: string;
}) {
  // window.open with noopener always returns null, so there is no handle
  // to check; a failed fetch shows as the server's JSON error in that tab.
  const open = () => {
    window.open(api.objectURL(obj.key), '_blank', 'noopener,noreferrer');
  };
  return (
    <tr className="obj">
      <td>{trimPrefix(obj.key, prefix) || obj.key}</td>
      <td style={{ textAlign: 'right' }}>{humanBytes(obj.size)}</td>
      <td>{new Date(obj.last_modified).toISOString().replace('T', ' ').slice(0, 16)}</td>
      <td>
        <button className="tab" onClick={open}>open</button>
      </td>
    </tr>
  );
}

function breadcrumbs(prefix: string): { label: string; value: string }[] {
  const out = [{ label: '/', value: '' }];
  if (!prefix) return out;
  const parts = prefix.replace(/\/$/, '').split('/');
  let acc = '';
  for (const p of parts) {
    acc += p + '/';
    out.push({ label: p, value: acc });
  }
  return out;
}

function trimPrefix(s: string, prefix: string): string {
  return s.startsWith(prefix) ? s.slice(prefix.length) : s;
}
